import { chain, get, isEmpty, isArray, sumBy } from "lodash"
import { isUnmodeled } from "helpers/securities"
import { buildContributorKey } from "helpers/contributors"
import { getFactorValueByMetricDisplay } from "./helpers/factor-utils"
import { getContributorLabel, NOT_HELD } from "../../connected-components/asset-view/helpers"
import { getGroupByQueryParameters, getGroupBySubqueryParameters, getGroupByFields, sanitizePositionSet, getIsNoLongerHeldFromContributor } from "../helpers"

const RETURNS_SUBQUERY = `
  cumulativePercent
  cumulativeCurrency
`

const ATTRIBUTION_SUMMARY_SUBQUERY = `
  summary {
    factorsReturn {
      ${RETURNS_SUBQUERY}
    }
    specificReturn {
      ${RETURNS_SUBQUERY}
    }
    tradingReturn {
      ${RETURNS_SUBQUERY}
    }
  }`

const ATTRIBUTION_FACTORS_SUBQUERY = `
  factors {
    id
    name
    category
    return {
      ${RETURNS_SUBQUERY}
    }
  }`

const CONTRIBUTORS_SUBQUERY = `
  id
  ticker
  mic
  description
  isUnmodeled
  modelProviderId
  assetClass
  notHeld
  country
  currency
  bloombergTicker
  sector
  totalReturn {
    ${RETURNS_SUBQUERY}
  }
  attribution {
    ${ATTRIBUTION_SUMMARY_SUBQUERY}
    ${ATTRIBUTION_FACTORS_SUBQUERY}
  }
`

const UNGROUPED_CONTRIBUTORS_SUBQUERY = `performanceContributors (includeNotHeld: $includeNotHeld) {
  ${CONTRIBUTORS_SUBQUERY}
}`

function getGroupedContributorsSubQuery(groupByFields) {
  return `
  groupedPerformanceContributors(${getGroupBySubqueryParameters(groupByFields)}, includeNotHeld: $includeNotHeld) {
    id
    name
    totalReturn {
      ${RETURNS_SUBQUERY}
    }
    attribution {
      ${ATTRIBUTION_SUMMARY_SUBQUERY}
      ${ATTRIBUTION_FACTORS_SUBQUERY}
    }
    contributors {
      ${CONTRIBUTORS_SUBQUERY}
    }
  }`
}

function getQuery(groupByFields) {
  const extraParameters = groupByFields ? getGroupByQueryParameters(groupByFields) : ""

  const query = `query ($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $from: Date!, $to: Date!, $includeNotHeld: Boolean!${extraParameters}) {
    model (id: $modelId) {
      simulation (positionSet: $positionSet, base: $base, from: $from, to: $to, interval: END_DATE) {
        performance {
          totalReturn {
            ${RETURNS_SUBQUERY}
          }
          attribution {
            summary {
              factors {
                ${RETURNS_SUBQUERY}
              }
              specific {
                ${RETURNS_SUBQUERY}
              }
              trading {
                ${RETURNS_SUBQUERY}
              }
            }
            ${ATTRIBUTION_FACTORS_SUBQUERY}
          }
        }
        ${groupByFields ? getGroupedContributorsSubQuery(groupByFields) : UNGROUPED_CONTRIBUTORS_SUBQUERY}
      }
    }
  }`
  return query
}

export function getPerformanceReturns({ totalReturn, attribution, ...contributor } = {}, options = {}) {
  if (!totalReturn && !attribution) {
    return {}
  }

  const metricDisplay = options.selectedMetricDisplay
  const attributionFactors = attribution?.factors || []

  return {
    descriptors: contributor,
    totalReturn: getFactorValueByMetricDisplay(totalReturn, metricDisplay),
    ...getSummaryReturns(attribution?.summary, metricDisplay),
    ...getFactorReturns(attributionFactors, metricDisplay),
  }
}

export function getSummaryReturns(summary, metricDisplay) {
  if (!summary || typeof summary !== "object") {
    return {}
  }
  return Object.fromEntries(
    Object.entries(summary).map(([key, value]) => [
      key.includes("Return") ? key : `${key}Return`,
      getFactorValueByMetricDisplay(value, metricDisplay),
    ]),
  )
}

export function getFactorReturns(factors, metricDisplay) {
  if (!isArray(factors)) {
    return {}
  }

  const individualReturns = chain(factors)
    .mapKeys(factor => factor?.id)
    .pickBy(factor => factor?.return)
    .mapValues(factor => getFactorValueByMetricDisplay(factor.return, metricDisplay))
    .value()

  const aggregatedReturns = chain(factors)
    .filter(factor => factor?.return && factor?.category)
    .groupBy("category")
    .mapValues(categoryFactors => {
      if (metricDisplay) {
        return sumBy(categoryFactors, factor => factor.return[metricDisplay] ?? 0)
      }
      return categoryFactors.reduce((acc, factor) => {
        Object.entries(factor.return).forEach(([key, value]) => {
          acc[key] = (acc[key] ?? 0) + value
        })
        return acc
      }, {})
    })
    .value()

  return {
    ...individualReturns,
    ...aggregatedReturns,
  }
}

export function parseContributors(prefix, contributors, options = {}) {
  if (!isArray(contributors)) {
    return {}
  }

  return contributors.reduce((acc, contributor) => {
    if (!contributor) {
      return acc
    }

    if (contributor.notHeld && options.notHeldRollup) {
      if (!acc[NOT_HELD]) {
        acc[NOT_HELD] = {
          label: "Not Held",
          notHeld: true,
          values: {
            [prefix]: {},
          },
        }
      }

      const performanceData = getPerformanceReturns(contributor, options)
      Object.entries(performanceData).forEach(([key, value]) => {
        if (typeof value === "number") {
          if (!acc[NOT_HELD].values[prefix][key]) {
            acc[NOT_HELD].values[prefix][key] = value
          } else {
            acc[NOT_HELD].values[prefix][key] += value
          }
        }
      })
    } else {
      acc[buildContributorKey(contributor)] = {
        label: getContributorLabel(contributor),
        notHeld: contributor.notHeld || false,
        isNoLongerHeld: getIsNoLongerHeldFromContributor(contributor),
        isUnmodeled: isUnmodeled(contributor),
        values: {
          [prefix]: getPerformanceReturns(contributor, options),
        },
      }
    }
    return acc
  }, {})
}

export function parseGroupedContributors(prefix, groups, options = {}) {
  if (!isArray(groups)) {
    return {}
  }

  return chain(groups)
    .filter(group => group && group.id)
    .keyBy("id")
    .mapValues(group => {
      return {
        label: group.name || group.id,
        values: {
          [prefix]: getPerformanceReturns(group, options),
        },
        children: parseContributors(prefix, group.contributors, options),
      }
    })
    .value()
}

export function createPerformanceContributorsQuery({ prefix, modelId, positionSet, base, from, to, groupByOptions, ...options }) {
  // We see if we're running intraday queries elsewhere and if we are, then we
  // ignore the base
  const { shouldUseIntradayQuery } = options

  if (!prefix || !modelId || !positionSet || !from || !to) {
    return {
      prefix: prefix || "default",
      key: ["performance", "contributors", prefix || "default"],
      query: "",
      variables: {},
      select: () => ({
        label: "Total",
        values: {},
        children: {},
      }),
    }
  }

  const groupByFields = getGroupByFields(groupByOptions)
  const { groupBy, groupById, tier } = groupByFields
  const sanitizedPositionSet = sanitizePositionSet(positionSet)

  const key = [
    "performance",
    "contributors",
    prefix,
    modelId,
    positionSet,
    from,
    to,
    groupBy,
    groupById,
    tier,
    shouldUseIntradayQuery,
    ...(shouldUseIntradayQuery ? [] : [base]),
  ]

  return {
    prefix,
    key,
    query: getQuery(groupBy && groupByFields),
    variables: {
      modelId,
      positionSet: sanitizedPositionSet,
      base: shouldUseIntradayQuery ? null : base,
      from,
      to,
      ...groupByFields,
      includeNotHeld: !!base && !shouldUseIntradayQuery,
    },
    select: (result) => {
      const total = getPerformanceReturns(get(result, "data.model.simulation.performance.0", {}), options)

      let children
      if (!isEmpty(total)) {
        children = groupBy
          ? parseGroupedContributors(prefix, result.data.model.simulation.groupedPerformanceContributors, options)
          : parseContributors(prefix, result.data.model.simulation.performanceContributors, options)
      }

      return {
        label: "Total",
        values: {
          [prefix]: total,
        },
        children: children || {},
      }
    },
  }
}
